import React, { FC, useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useStores } from "../hooks/useStores.hook";
import { observer } from "mobx-react-lite";
import { ApiEndpointPath, getApiEndpoint } from "../stores/apiStore";

const useLocalStorage = (storageKey, fallbackState) => {
  const initialState = localStorage.getItem(storageKey)
    ? JSON.parse(localStorage.getItem(storageKey)!) ?? fallbackState
    : fallbackState;
  const [value, setValue] = React.useState(initialState);

  React.useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(value));
  }, [value, storageKey]);

  return [value, setValue];
};

export const JwtProvider: FC<{}> = observer(({ children }) => {
  const { apiStore } = useStores();
  const [hasPerformedInitialCheck, setHasPerformedInitialCheck] = useState(false);
  const [jwt, setJwt] = useLocalStorage("JWT_KEY", null);

  useEffect(() => {
    const authenticate = async () => {
      try {
        // 1. Is the stored JWT still valid?
        const authResponse = await apiStore.authenticatedFetch(
          getApiEndpoint(ApiEndpointPath.JWT_AUTHENTICATOR),
          {},
          jwt,
        );
        if (authResponse.status === 200) {
          apiStore.setJwt(jwt);
        } else {
          // 2. Fetch a fresh JWT from the backend. Note the backend signals "no JWT available"
          // with a 200 and an empty/absent jwt (not a 401), so we must check the body, not just
          // the status — and guard json() since the body may be empty.
          const jwtResponse = await apiStore.authenticatedFetch(getApiEndpoint(ApiEndpointPath.JWT), {}, jwt);
          const body = jwtResponse.status === 200 ? await jwtResponse.json().catch(() => null) : null;
          const freshJwt = body?.jwt;
          if (freshJwt) {
            setJwt(freshJwt); // persist to localStorage["JWT_KEY"]
            apiStore.setJwt(freshJwt); // unlock the app this session
          }
          // else: no usable JWT → apiStore.jwt stays "" → Unauthorized message renders
        }
      } catch (e) {
        // Network/CORS failure (e.g. a CORS-blocked 401) rejects the fetch — swallow it so we
        // still reach the render below with an empty jwt → Unauthorized, rather than hanging blank.
        console.error("JWT authentication failed", e);
      } finally {
        // Always flip this, even on a thrown/rejected fetch, so the page never stays blank.
        setHasPerformedInitialCheck(true);
      }
    };

    authenticate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!hasPerformedInitialCheck) {
    return <></>;
  }
  if (apiStore.jwt) {
    return <>{children}</>;
  } else {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Typography variant="h5">Unauthorized — you do not have access to view this page.</Typography>
      </Box>
    );
  }
});
